import { useQuery } from '@apollo/client'
import { AddCircle, RemoveCircle } from '@mui/icons-material'
import { IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { type GridColumnGroupingModel } from '@mui/x-data-grid-pro'
import { formatDate } from 'date-fns'
import { type FC, useMemo } from 'react'
import { GetRostersByIds } from 'shared/components/RostersAutocomplete/RostersAutocomplete.gql'
import { useCandidateSearchUrlState } from 'shared/hooks/candidateSearchUrlState'
import { useReferenceSearchesUrlState } from 'shared/hooks/useReferenceSearchesUrlState'
import { type CandidateTableColumnDefinition } from 'recruiter/components/candidateTableColumns'
import { generateRowId } from 'recruiter/components/RosterCandidatesSearchResultsTable'
import ReferenceStatusInlineEditCell from 'recruiter/pages/Rosters/RosterProfile/EditCells/ReferenceStatusInlineEditCell'
import { useRosterSearch } from './RosterSearchContext'

const EMPTY_ROSTERS: readonly never[] = []

const ReferenceStatusCell: FC<{ row: any; refRosterId: string }> = ({
    row,
    refRosterId,
}) => {
    const { canEdit, onOpenNoteDrawer } = useRosterSearch()
    const refCandidate = row.referenceRosterCandidates?.find(
        (rrc: { rosterId: string }) => rrc.rosterId === refRosterId
    )
    return (
        <ReferenceStatusInlineEditCell
            refRosterId={refRosterId}
            candidateId={row.candidateId}
            rosterCandidateId={refCandidate?.rosterCandidateId}
            currentStatus={refCandidate?.processStatus ?? undefined}
            currentStatusLabel={refCandidate?.processStatusLabel ?? undefined}
            currentSummaryGroup={refCandidate?.summaryGroup ?? undefined}
            currentSummaryGroupLabel={
                refCandidate?.summaryGroupLabel ?? undefined
            }
            canEdit={canEdit}
            rowId={generateRowId(row)}
            onOpenNoteDrawer={onOpenNoteDrawer}
        />
    )
}

export interface RosterSearchColumnsResult {
    columns: CandidateTableColumnDefinition[]
    columnGroupingModel: GridColumnGroupingModel
}

export function useRosterSearchColumns(
    templateColumns: CandidateTableColumnDefinition[]
): RosterSearchColumnsResult {
    const { refColumnRosterIds, setRefColumnRosterIds, removeRosterId } =
        useReferenceSearchesUrlState()
    const { filters } = useCandidateSearchUrlState()

    const refColumnSet = new Set(refColumnRosterIds)
    const suggestedRosterIds = filters.searchFilters
        .map((f) => f.rosterId)
        .filter((id): id is string => !!id && !refColumnSet.has(id))

    const { data: rostersData } = useQuery(GetRostersByIds, {
        variables: { ids: refColumnRosterIds },
        skip: refColumnRosterIds.length === 0,
    })

    // using EMPTY_ROSTERS for useMemo
    const refRosters = rostersData?.rostersByIds ?? EMPTY_ROSTERS

    // eslint-disable-next-line react-hooks/exhaustive-deps -- removeRosterId is a new ref every render
    // (closes over URL state) but is only used in onClick handlers, not during render.
    // refRosters changing guarantees a fresh closure when it actually matters.
    return useMemo(() => {
        const referenceColumns: CandidateTableColumnDefinition[] =
            refRosters.flatMap((roster) => {
                const findRefCandidate = (row: any) =>
                    row.referenceRosterCandidates?.find(
                        (rrc: { rosterId: string }) =>
                            rrc.rosterId === roster.id
                    )

                return [
                    {
                        field: `ref_status_${roster.id}`,
                        headerName: 'Status',
                        width: 150,
                        sortable: true,
                        referenceRosterId: roster.id,
                        referenceSortField: 'refProcessStatus',
                        renderCell: (params) => (
                            <ReferenceStatusCell
                                row={params.row}
                                refRosterId={roster.id}
                            />
                        ),
                    },
                    {
                        field: `ref_processStatusUpdatedAt_${roster.id}`,
                        headerName: 'Process Status Updated',
                        width: 170,
                        sortable: true,
                        referenceRosterId: roster.id,
                        referenceSortField: 'refProcessStatusUpdated',
                        valueGetter: (_value, row) => {
                            const ref = findRefCandidate(row)
                            return ref?.processStatusUpdatedAt
                                ? formatDate(
                                      ref.processStatusUpdatedAt,
                                      'MM/dd/yyyy'
                                  )
                                : '-'
                        },
                    },
                    {
                        field: `ref_internalNote_${roster.id}`,
                        headerName: 'Internal Note',
                        width: 200,
                        sortable: true,
                        referenceRosterId: roster.id,
                        referenceSortField: 'refInternalNote',
                        valueGetter: (_value, row) => {
                            const ref = findRefCandidate(row)
                            return ref?.internalNote ?? '-'
                        },
                    },
                ] as CandidateTableColumnDefinition[]
            })

        const columnGroupingModel: GridColumnGroupingModel = refRosters.map(
            (roster) => ({
                groupId: `ref_group_${roster.id}`,
                children: [
                    { field: `ref_status_${roster.id}` },
                    { field: `ref_processStatusUpdatedAt_${roster.id}` },
                    { field: `ref_internalNote_${roster.id}` },
                ],
                renderHeaderGroup: () => (
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                        gap={1}
                    >
                        <Tooltip title={roster.rosterName}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {roster.rosterName}
                            </Typography>
                        </Tooltip>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation()
                                removeRosterId(roster.id)
                            }}
                            sx={{ p: 0.25 }}
                        >
                            <RemoveCircle
                                color="error"
                                sx={{ fontSize: '1rem' }}
                            />
                        </IconButton>
                    </Stack>
                ),
            })
        )

        const processStatusIndex = templateColumns.findIndex(
            (col) => col.field === 'processStatus'
        )
        let columns: CandidateTableColumnDefinition[]
        if (processStatusIndex === -1) {
            columns = [...templateColumns, ...referenceColumns]
        } else {
            columns = [
                ...templateColumns.slice(0, processStatusIndex + 1),
                ...referenceColumns,
                ...templateColumns.slice(processStatusIndex + 1),
            ]
        }

        if (suggestedRosterIds.length > 0) {
            const psIdx = processStatusIndex === -1 ? -1 : processStatusIndex
            if (psIdx !== -1) {
                const psCol = columns[psIdx]
                columns[psIdx] = {
                    ...psCol,
                    renderHeader: () => (
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                            gap={0.5}
                        >
                            <Typography variant="subtitle2">
                                {psCol.headerName}
                            </Typography>
                            <Tooltip title="Add filtered searches as reference columns">
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setRefColumnRosterIds([
                                            ...refColumnRosterIds,
                                            ...suggestedRosterIds,
                                        ])
                                    }}
                                    sx={{ p: 0.25 }}
                                >
                                    <AddCircle
                                        color="primary"
                                        sx={{ fontSize: '1rem' }}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    ),
                }
            }
        }

        return { columns, columnGroupingModel }
    }, [templateColumns, refRosters, suggestedRosterIds])
}
